<template>
  <Toast />
  <div class="main-container px-1 md:px-8">
    <div class=" mt-2 flex flex-wrap items-center gap-4">
      <div class="flex w-full gap-4 md:w-[100%] lg:w-[45%]">
        <InputGroup class="!flex-1">
          <InputText id="advertiser-search" v-model="advertiserSearch" placeholder="Advertiser Name" class="w-full" />
        </InputGroup>
        <Button severity="secondary" outlined icon="pi pi-sort" aria-label="Save" class="!w-[80px] lg:!hidden" />
      </div>
      <div class="flex gap-4 w-full md:w-[100%] lg:w-auto lg:flex-1">
        <MultiSelect id="market-select" v-model="selectedMarkets" :options="marketOptions" optionLabel="label"
          optionValue="value" placeholder="Markets" :maxSelectedLabels="2" class="w-full md:!w-[50%] lg:!w-[45%]"
          :multiple="true" display="chip">
          <!-- Selected Value Template (Chips) -->
          <template #value="slotProps">
            <div v-if="slotProps.value.length > 0">
              <template v-if="slotProps.value.length <= 2">
                <div v-for="option in slotProps.value" :key="option" class="inline-flex items-center mr-2">
                  <span :class="`fi fi-${option.toLowerCase()}`" style="width: 24px; height: 18px;" class="mr-2"></span>
                  <span>{{ option === 'INT' ? 'Global' : getCountryName(option) }}</span>
                </div>
              </template>
              <span v-else>
                {{ slotProps.value.length }} geos selected
              </span>
            </div>
            <span v-else>Markets</span>
          </template>

          <!-- Option Template -->
          <template #option="slotProps">
            <div class="flex items-center">
              <span :class="`fi fi-${slotProps.option.value.toLowerCase()}`" style="width: 24px; height: 18px;"
                class="mr-2">
              </span>
              <div>{{ slotProps.option.label }}</div>
            </div>
          </template>

          <!-- Selected Items Template -->
          <template #chip="slotProps">
            <div class="flex items-center">
              <span :class="`fi fi-${slotProps.value.toLowerCase()}`" style="width: 24px; height: 18px;" class="mr-2">
              </span>
              <span>{{ slotProps.value === 'INT' ? 'Global' : getCountryName(slotProps.value) }}</span>
            </div>
          </template>
        </MultiSelect>
        <Select id="vertical-select" v-model="selectedVertical" :options="verticalOptions" optionLabel="name"
          optionValue="code" placeholder="Vertical" showClear class="w-full md:!w-[50%] lg:!w-[45%]" />
      </div>
      <Button icon="pi pi-sort" aria-label="Save" class="!hidden lg:!block !w-[50px]" severity="secondary" outlined />
    </div>

    <div
      class="mt-4 grid gap-4 grid-cols-[repeat(auto-fill,minmax(360px,5fr))] max-h-[calc(100svh-200px)] overflow-y-auto">
      <div v-for="(advertiser, index) in filteredVisibleItems" :key="index" class="card-bg p-4 border border-gray-200 rounded-lg shadow-md 
          transition-all duration-300 ease-in-out 
          hover:shadow-xl hover:-translate-y-2 
          hover:border-primary-500 hover:scale-[1.02] 
          hover:bg-opacity-90 
          active:translate-y-0 active:scale-100">
        <div class="title-section flex items-start cursor-pointer mt-2 ">
          <!-- <Skeleton width="6rem" height="1rem" shape="circle" size="3rem" class="mr-4"></Skeleton>
            <div class="font-semibold text-xl">{{ advertiser.name }}</div> -->
          <div class="flex items-start justify-start w-full bg-white rounded-xl min-h-[100px]"
            @click="onAdvertiserCardClick(advertiser)">
            <div class="w-full h-16 overflow-hidden flex items-center justify-center my-auto">
              <template v-if="advertiser.logoUrl">
                <img :src="advertiser.logoUrl" :alt="advertiser.name"
                  class="object-contain w-auto h-[70%] w-[50%] my-auto" />
              </template>
              <template v-else>
                <div class="flex items-center justify-center h-[70%] w-[50%] my-auto bg-gray-100 rounded">
                  <span class="text-gray-700 font-medium text-center">{{ advertiser.name }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div
          class="deal-section flex flex-col items-start justify-center px-3 pt-3 max-w-md overflow-hidden min-h-[80px] ">
          <div class="overflow-y-auto max-h-[60px] w-full justify-center"
            style="scrollbar-width: none; -ms-overflow-style: none; cursor: grab;"
            onmousedown="this.style.cursor='grabbing'" onmouseup="this.style.cursor='grab'">
            <div v-for="deal in getDealsToShow(advertiser).value" :key="deal.dealId"
              class="deal-row grid grid-cols-[auto,0.8fr,auto] gap-4 items-center w-full cursor-pointer"
              @click="onDealClick(advertiser, deal)">

              <!-- Deal Description -->
              <div class="col-span-1 hover:underline font-bolyar flex justify-center w-full">
                <div class="text-md pl-2 truncate text-center">
                  {{ deal.dealDescription }}*
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="market-section flex space-x-2 pl-6">
          <span v-for="marketName in advertiser.markets" :key="`${advertiser.id}-${marketName}`"
            :class="marketClass(marketName)">
          </span>
        </div>
      </div>
    </div>
    <div class="text-end mt-2 mr-10">
      * All deals are negotiable and subject to approval by our team.
    </div>
    <div ref="sentinel" class="h-4"></div>

    <Drawer v-model:visible="drawerVisible" position="right" class="!w-full md:!w-[80vw] ">
      <template #container="{ closeCallback }">
        <span class="pl-10 pt-5">
          <Button severity="secondary" type="button" @click="closeCallback" icon="pi pi-times" rounded
            outlined></Button>
        </span>
        <AdvertiserDetails :advertiser="selectedAdvertiser" :deal="selectedDeal" />
        <!-- <Button class="w-20 mt-10" @click="closeCallback">Close</Button> -->
      </template>
    </Drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import AdvertiserService from '@/services/AdvertiserService';
import { marketClass } from '@/utils/markets';
import { getFriendlyDealMessage } from '@/utils/deals';
import AdvertiserDetails from './AdvertiserDetails.vue';
import { marketOptions } from '@/utils/markets';
import { getCountryName } from '@/utils/countries';
import { verticals, getVerticalName } from '@/utils/verticals';

const allItems = ref([]); // Stores all items initially fetched
const visibleItems = ref([]); // Controls items rendered on screen
const itemsPerLoad = 40; // Number of items to add each time the sentinel is triggered
const drawerVisible = ref(false);

const selectedAdvertiser = ref({});
const selectedDeal = ref(null);

const advertiserSearch = ref('');
const selectedMarkets = ref([]);
const selectedVertical = ref(null);

// Check if any filters are active
const isFiltering = computed(() => {
  return !!advertiserSearch.value ||
    selectedMarkets.value.length > 0 ||
    !!selectedVertical.value;
});

// Filter all items when filtering, otherwise use visibleItems for infinite scroll
const filteredVisibleItems = computed(() => {
  // If no filters active, return current visibleItems (for infinite scroll)
  if (!isFiltering.value) {
    return visibleItems.value;
  }

  // If filtering, search through all items
  let filtered = allItems.value;

  if (advertiserSearch.value) {
    filtered = filtered.filter(advertiser =>
      advertiser.name.toLowerCase().includes(advertiserSearch.value.toLowerCase())
    );
  }

  if (selectedVertical.value) {
    console.log(selectedVertical.value);
    console.log(filtered);
    filtered = filtered.filter(advertiser =>
      advertiser.verticals.includes(selectedVertical.value) // Match against the vertical code
    );
  }

  if (selectedMarkets.value.length > 0) {
    filtered = filtered.filter(advertiser =>
      selectedMarkets.value.some(market => advertiser.markets.includes(market))
    );
  }
  return filtered;
});

// Watch for changes in filters and update visibleItems
watch([advertiserSearch, selectedMarkets], () => {
  // Reset visibleItems with first chunk of filtered results
  if (!isFiltering.value) {
    visibleItems.value = allItems.value.slice(0, itemsPerLoad);
  }
});

// Common mapping function for deals
const mapDealToViewModel = (deal, marketName) => ({
  id: deal.dealId,
  dealType: deal.dealType,
  commissionType: deal.customerDeal.commissionType,
  dealDescription: getFriendlyDealMessage(deal.customerDeal),
  marketName: marketName,
  isPublic: deal.isPublic
});

// Helper method to find the best deal by CPA from a list of deals
const getBestDealByCpa = (deals) => {
  if (!deals || deals.length === 0) {
    return null;
  }

  return deals.reduce((best, current) => {
    // Extract CPA value from deal
    const getCpaValue = (deal) => {
      if (deal.customerDeal && deal.customerDeal.cpa) {
        return parseFloat(deal.customerDeal.cpa);
      }
      // Fallback to parsing from description
      const cpaMatch = deal.dealDescription?.match(/€(\d+(\.\d+)?)\s*CPA/i);
      return cpaMatch ? parseFloat(cpaMatch[1]) : 0;
    };

    const currentCpa = getCpaValue(current);
    const bestCpa = getCpaValue(best);
    return currentCpa > bestCpa ? current : best;
  }, deals[0]);
};

// Now you can use this with both getFilteredDeals and getToplistDeals
const getToplistDeals = (advertiser) => {
  return computed(() => {
    if (!advertiser || !advertiser.marketDeals) {
      return [];
    }

    const deals = advertiser.marketDeals.flatMap((market) =>
      market.deals
        .filter((deal) => deal.toplist)
        .map((deal) => mapDealToViewModel(deal, market.marketName))
    );

    // Get the best deal by CPA
    const bestDeal = getBestDealByCpa(deals);

    // Return as an array with one item, or empty if no deals found
    return bestDeal ? [bestDeal] : [];
  });
};

const getFilteredDeals = (advertiser) => {
  return computed(() => {
    if (!advertiser || !advertiser.marketDeals) {
      return [];
    }

    const deals = advertiser.marketDeals
      .filter(market =>
        selectedMarkets.value.length === 0 ||
        selectedMarkets.value.includes(market.marketName)
      )
      .flatMap((market) =>
        market.deals
          .filter((deal) =>
            (!selectedVertical.value || deal.vertical === selectedVertical.value)
          )
          .map((deal) => mapDealToViewModel(deal, market.marketName))
      );

    // Get the best deal by CPA
    const bestDeal = getBestDealByCpa(deals);

    // Return as an array with one item, or empty if no deals found
    return bestDeal ? [bestDeal] : [];
  });
};

const getDealsToShow = (advertiser) => {
  return computed(() => {
    return isFiltering.value ? getFilteredDeals(advertiser).value : getToplistDeals(advertiser).value;
  });
};

const onAdvertiserCardClick = (advertiser) => {
  selectedAdvertiser.value = advertiser;
  selectedDeal.value = advertiser.marketDeals[0]; // Reset selected deal
  drawerVisible.value = true;
}

const onDealClick = (advertiser, deal) => {
  selectedDeal.value = deal;
  selectedAdvertiser.value = advertiser;
  drawerVisible.value = true;
}

// Update the loadMoreItems function to only work when not filtering
const loadMoreItems = () => {
  if (!isFiltering.value && visibleItems.value.length < allItems.value.length) {
    const nextIndex = visibleItems.value.length;
    const newItems = allItems.value.slice(nextIndex, nextIndex + itemsPerLoad);
    visibleItems.value.push(...newItems);
  }
};

// Truncate text by word count
const truncateWords = (text, wordLimit) => {
  const words = text.split(' ');
  return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : text;
};

// Generate dynamic height for each card
const heightOptions = ['10rem', '12rem', '14rem', '16rem', '18rem']; // Define a set of heights
const getDynamicHeight = (index) => {
  return heightOptions[index % heightOptions.length]; // Cycle through heights based on index
};

// Pattern-based flex-grow values
const flexGrowValues = ['flex-grow-1', 'flex-grow-2', 'flex-grow-1'];
const getFlexGrow = (index) => {
  return flexGrowValues[index % flexGrowValues.length];
};

// Initial setup and intersection observer
const scrollContainer = ref(null);
const sentinel = ref(null);

// Add computed property for vertical options
const verticalOptions = computed(() => {
  return verticals.map(vertical => ({
    name: vertical.name,
    code: vertical.code
  }));
});

// Add a watcher to debug
watch(selectedVertical, (newValue) => {
  console.log('Selected vertical changed:', newValue);
});

async function fetchAdvertisers() {
  try {
    await AdvertiserService.getAllForUser().then((response) => {
      allItems.value = response.data;
      visibleItems.value = allItems.value.slice(0, itemsPerLoad); // Render the first chunk
    });
  } catch (err) {
    console.error(err);
  } finally {
  }
}

onMounted(() => {
  fetchAdvertisers();

  // Set up the Intersection Observer to watch the sentinel
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && visibleItems.value.length < allItems.value.length) {
        console.log('Loading more items');
        loadMoreItems();
      }
    },
    { root: scrollContainer.value, rootMargin: '200px' }
  );

  if (sentinel.value) {
    observer.observe(sentinel.value);
  }
});
</script>

<style>
.main-container .p-inputgroup {
  width: inherit;
}

.p-multiselect-token {
  padding: 0.25rem 0.5rem;
}
</style>